import React, { useState, useMemo } from 'react';
import {
  CCard, CCardHeader, CCardBody, CRow, CCol, CFormInput, CFormSelect,
  CButton, CBadge, CListGroup, CListGroupItem
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilFilter, cilReload } from '@coreui/icons';
import { AUDIO_COLOR_LEGEND, getAudioSegmentColor } from '../../utils/audioUtils';
import dayjs from 'dayjs';

const safeString = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return Object.values(val).join('; ');
    return String(val);
};

const AudioSearchDashboard = ({ segments, suggestions, onSegmentSelect, availableCameras = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCamera, setFilterCamera] = useState('ALL');
  const [filterContext, setFilterContext] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL'); 
  const [filterSentiment, setFilterSentiment] = useState('ALL');
  const [filterAggression, setFilterAggression] = useState('ALL');
  const [filterTopicTag, setFilterTopicTag] = useState('ALL');
  const [filterDuration, setFilterDuration] = useState('ALL');

  const handleResetFilters = () => {
    setSearchQuery('');
    setFilterCamera('ALL');
    setFilterContext('ALL');
    setFilterCategory('ALL');
    setFilterSentiment('ALL');
    setFilterAggression('ALL');
    setFilterTopicTag('ALL');
    setFilterDuration('ALL');
  };

  const filteredSegments = useMemo(() => {
    if (!segments) return [];

    return segments.filter((seg) => {
      const q = searchQuery.toLowerCase().trim();
      const markers = seg.dialog_markers || {};
      const sentiment = (markers.sentiment || '').toLowerCase();
      const topics = seg.topics || {};
      const dur = seg.duration_sec || 0;

      if (filterCamera !== 'ALL' && seg.camera_name !== filterCamera) return false;

      if (q) {
        const matchEntities = (markers.key_entities || []).some(e => e.toLowerCase().includes(q));
        // Безопасный поиск по темам
        const matchTopics = Object.values(topics).some(v => v && String(JSON.stringify(v)).toLowerCase().includes(q));
        const matchContext = (seg.context_type || '').toLowerCase().includes(q);
        const matchRaw = (seg.raw_lines || []).some(l => (l.text || '').toLowerCase().includes(q));

        if (!matchEntities && !matchTopics && !matchContext && !matchRaw) return false;
      }

      if (filterContext === 'CLIENT' && !seg.context_type.toLowerCase().includes('клиент')) return false;
      if (filterContext === 'FLORIST' && !seg.context_type.toLowerCase().includes('флорист')) return false;
      if (filterContext === 'INTERNAL' && !seg.context_type.toLowerCase().includes('внутренний')) return false;

      if (filterCategory === 'WORK' && topics.personal_and_atmosphere && !topics.order_details && !topics.client_interaction && !topics.materials_search) return false;
      if (filterCategory === 'PERSONAL' && !topics.personal_and_atmosphere) return false;

      if (filterSentiment === 'POSITIVE' && !sentiment.includes('позитив')) return false;
      if (filterSentiment === 'NEUTRAL' && !sentiment.includes('нейтральн')) return false;
      if (filterSentiment === 'TENSE' && !sentiment.includes('напряжен') && !sentiment.includes('конфликт')) return false;

      if (filterAggression === 'YES' && !markers.has_profanity_or_aggression) return false;
      if (filterAggression === 'NO' && markers.has_profanity_or_aggression) return false;

      if (filterTopicTag === 'MATERIALS' && !topics.materials_search) return false;
      if (filterTopicTag === 'ORDERS' && !topics.order_details) return false;
      if (filterTopicTag === 'CLIENTS' && !topics.client_interaction) return false;
      if (filterTopicTag === 'MANAGEMENT' && !topics.management_and_internal) return false;
      if (filterTopicTag === 'PERSONAL' && !topics.personal_and_atmosphere) return false;
      if (filterTopicTag === 'OPERATIONS' && !topics.operations) return false;

      if (filterDuration === 'SHORT' && dur >= 30) return false;
      if (filterDuration === 'MEDIUM' && (dur < 30 || dur > 120)) return false;
      if (filterDuration === 'LONG' && dur <= 120) return false;

      return true;
    });
  }, [segments, searchQuery, filterCamera, filterContext, filterCategory, filterSentiment, filterAggression, filterTopicTag, filterDuration]);

  return (
    <div className="mb-4">
      <CCard className="mb-3 shadow-sm border-0">
        <CCardHeader className="bg-white fw-bold">🎨 Легенда цветов аудио-дорожки:</CCardHeader>
        <CCardBody className="py-2">
          <div className="d-flex flex-wrap gap-3">
            {AUDIO_COLOR_LEGEND.map((item, idx) => (
              <div key={idx} className="d-flex align-items-center gap-2 small">
                <span style={{ width: '14px', height: '14px', backgroundColor: item.color, borderRadius: '3px' }}></span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </CCardBody>
      </CCard>

      <CCard className="shadow-sm">
        <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
          <span><CIcon icon={cilFilter} className="me-2" />Расширенные фильтры диалогов</span>
          <CButton color="secondary" variant="ghost" size="sm" onClick={handleResetFilters}>
            <CIcon icon={cilReload} className="me-1" /> Сбросить фильтры
          </CButton>
        </CCardHeader>
        <CCardBody>
          <CRow className="g-3 mb-3">
            <CCol md={availableCameras.length > 0 ? 3 : 4}>
              <label className="form-label small fw-bold">Поиск (слова, имена):</label>
              <CFormInput
                list="search-suggestions"
                placeholder="Текст..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <datalist id="search-suggestions">
                {suggestions.map((sug, i) => <option key={i} value={sug} />)}
              </datalist>
            </CCol>

            {availableCameras.length > 0 && (
              <CCol md={3}>
                <label className="form-label small fw-bold">Источник звука:</label>
                <CFormSelect value={filterCamera} onChange={(e) => setFilterCamera(e.target.value)}>
                  <option value="ALL">Все микрофоны</option>
                  {availableCameras.map((camName, idx) => (
                    <option key={idx} value={camName}>🎙️ {camName}</option>
                  ))}
                </CFormSelect>
              </CCol>
            )}

            <CCol md={availableCameras.length > 0 ? 3 : 4}>
              <label className="form-label small fw-bold">Участники:</label>
              <CFormSelect value={filterContext} onChange={(e) => setFilterContext(e.target.value)}>
                <option value="ALL">Все</option>
                <option value="CLIENT">🤝 С клиентом</option>
                <option value="FLORIST">🌸 Флористы</option>
                <option value="INTERNAL">🗣️ Внутренние</option>
              </CFormSelect>
            </CCol>

            <CCol md={availableCameras.length > 0 ? 3 : 4}>
              <label className="form-label small fw-bold">Категория:</label>
              <CFormSelect value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="ALL">Все</option>
                <option value="WORK">💼 Рабочие вопросы</option>
                <option value="PERSONAL">☕ Личное общение</option>
              </CFormSelect>
            </CCol>

            <CCol md={3}>
              <label className="form-label small fw-bold">Тема разговора:</label>
              <CFormSelect value={filterTopicTag} onChange={(e) => setFilterTopicTag(e.target.value)}>
                <option value="ALL">Все темы</option>
                <option value="MATERIALS">🌸 Цветы, дефицит</option>
                <option value="ORDERS">💐 Детали заказа</option>
                <option value="CLIENTS">🤝 Взаимодействие</option>
                <option value="MANAGEMENT">🏢 Управление</option>
                <option value="PERSONAL">☕ Личное</option>
                <option value="OPERATIONS">🚚 Касса/Поставки</option>
              </CFormSelect>
            </CCol>

            <CCol md={3}>
              <label className="form-label small fw-bold">Эмоциональный настрой:</label>
              <CFormSelect value={filterSentiment} onChange={(e) => setFilterSentiment(e.target.value)}>
                <option value="ALL">Любой</option>
                <option value="POSITIVE">🟢 Позитивный</option>
                <option value="NEUTRAL">🔵 Нейтральный</option>
                <option value="TENSE">🔴 Конфликтный</option>
              </CFormSelect>
            </CCol>

            <CCol md={3}>
              <label className="form-label small fw-bold">Агрессия / Мат:</label>
              <CFormSelect value={filterAggression} onChange={(e) => setFilterAggression(e.target.value)}>
                <option value="ALL">Неважно</option>
                <option value="YES">⚠️ Только с матом</option>
                <option value="NO">✅ Чистая речь</option>
              </CFormSelect>
            </CCol>

            <CCol md={3}>
              <label className="form-label small fw-bold">Длительность:</label>
              <CFormSelect value={filterDuration} onChange={(e) => setFilterDuration(e.target.value)}>
                <option value="ALL">Любая</option>
                <option value="SHORT">⚡ &lt; 30 сек</option>
                <option value="MEDIUM">⏱️ 30с – 2м</option>
                <option value="LONG">📜 &gt; 2 мин</option>
              </CFormSelect>
            </CCol>
          </CRow>

          <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
            <h6 className="text-primary m-0">Найдено фрагментов: <strong>{filteredSegments.length}</strong></h6>
          </div>

          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <CListGroup flush>
              {filteredSegments.length === 0 ? (
                <CListGroupItem className="text-center text-muted py-4">Речевые фрагменты не найдены.</CListGroupItem>
              ) : (
                filteredSegments.map((seg) => {
                  const color = getAudioSegmentColor(seg);
                  // ОТОБРАЖЕНИЕ ДАТЫ И ВРЕМЕНИ В СПИСКЕ
                  const dateTimeStr = dayjs(seg.start_time).format('DD.MM.YYYY HH:mm:ss');
                  const markers = seg.dialog_markers || {};
                  const topics = seg.topics || {};

                  return (
                    <CListGroupItem key={seg.id} className="d-flex justify-content-between align-items-center cursor-pointer hover-bg-light py-2" style={{ borderLeft: `6px solid ${color}` }} onClick={() => onSegmentSelect(seg)}>
                      <div className="me-3" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <strong className="text-dark">{dateTimeStr}</strong>
                          {seg.camera_name && <CBadge color="dark" className="small">🎙️ {seg.camera_name}</CBadge>}
                          <CBadge color="secondary" className="small">{seg.context_type}</CBadge>
                          <span className="small text-muted">({seg.duration_sec} сек.)</span>
                          {markers.has_profanity_or_aggression && <CBadge color="danger">⚠️ Мат/Агрессия</CBadge>}
                        </div>
                        <div className="small text-truncate text-dark" style={{ maxWidth: '700px' }}>
                          {/* БЕЗОПАСНЫЙ РЕНДЕР СТРОКИ */}
                          {safeString(topics.client_interaction) || safeString(topics.management_and_internal) || safeString(topics.order_details) || safeString(topics.personal_and_atmosphere) || safeString(topics.materials_search) || 'Разговор'}
                        </div>
                      </div>
                      <CButton color="primary" size="sm" variant="outline" className="text-nowrap">Слушать</CButton>
                    </CListGroupItem>
                  );
                })
              )}
            </CListGroup>
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default AudioSearchDashboard;